import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, ChevronLeft } from "lucide-react";
import type {
  PassageAnswer,
  PassagePage,
  ReadingAnswer,
  ReadingQuestion,
} from "@examing/types";
import { RenderedHtml } from "modules/editor";
import { getQuestionOptionLabel } from "lib/questionOptions";
import { cn } from "lib/utils";
import { RadioGroup, RadioGroupItem } from "components/ui/RadioGroup";
import { Checkbox } from "components/ui/checkbox";
import { Button } from "components/ui/button";
import { FillInBlanksPlayer } from "./FillInBlanksPlayer";
import { MatchingPlayer } from "./MatchingPlayer";
import { resolvePassagePages, estimateReadingStats } from "./passagePages";

interface Props {
  passageText: string;
  passageTitle?: string;
  subQuestions: ReadingQuestion[];
  pages?: PassagePage[];
  value: PassageAnswer;
  onChange: (value: PassageAnswer) => void;
  disabled?: boolean;
  /** Wraps the passage body of the shown page, e.g. to add highlighting. */
  renderPassage?: (passage: React.ReactNode, pageIndex: number) => React.ReactNode;
  /** Numbers already used by earlier questions in the test; sub-questions continue from there. */
  numberOffset?: number;
  /** Scrolls to a sub-question; bump `seq` to repeat a jump to the same one. */
  focusRequest?: { questionId: string; seq: number };
  /** Sub-questions marked for review; with `onToggleFlag`, each question gets a flag button. */
  flaggedIds?: ReadonlySet<string>;
  onToggleFlag?: (questionId: string) => void;
}

const asMap = (value: ReadingAnswer | undefined): Record<string, string> =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

type TextSize = "sm" | "md" | "lg";

const TEXT_SIZES: { size: TextSize; title: string; glyph: string }[] = [
  { size: "sm", title: "Small text", glyph: "text-[11px]" },
  { size: "md", title: "Normal text", glyph: "text-[13px]" },
  { size: "lg", title: "Large text", glyph: "text-[15px]" },
];

const optionRowClass = (isSelected: boolean, disabled: boolean) =>
  cn(
    "-ml-2.5 flex min-h-11 items-center gap-3 rounded-lg px-2.5 py-2 text-sm leading-relaxed text-foreground transition-colors",
    disabled ? "cursor-default" : "cursor-pointer",
    isSelected ? "bg-muted font-medium" : !disabled && "hover:bg-muted/60",
  );

export function isReadingAnswerComplete(
  question: ReadingQuestion,
  answer?: ReadingAnswer,
): boolean {
  if (question.type === "MATCHING")
    return Boolean(
      question.matchPairs?.length &&
      question.matchPairs.every((pair) => asMap(answer)[pair.key]?.trim()),
    );
  if (question.type === "FILL_IN_BLANKS" && question.blanks?.length)
    return question.blanks.every((blank) =>
      Boolean(asMap(answer)[blank.id]?.trim()),
    );
  return Array.isArray(answer)
    ? answer.length > 0
    : typeof answer === "string" && Boolean(answer.trim());
}

export function PassagePlayer({
  passageText,
  passageTitle,
  subQuestions,
  pages: initialPages,
  value,
  onChange,
  disabled = false,
  renderPassage,
  numberOffset = 0,
  focusRequest,
  flaggedIds,
  onToggleFlag,
}: Props) {
  const { t } = useTranslation();
  const id = useId();

  // 1. Resolve structured pages from pages prop or subQuestions
  const pagesList: PassagePage[] = useMemo(
    () =>
      resolvePassagePages({
        pages: initialPages,
        subQuestions,
        passageText,
        passageTitle,
        defaultTitle: (idx) => `${t("reading_passage")} ${idx + 1}`,
      }),
    [initialPages, subQuestions, passageText, passageTitle, t],
  );

  const [activePageIndex, setActivePageIndex] = useState(0);
  const [textSize, setTextSize] = useState<TextSize>("md");
  const [mobileTab, setMobileTab] = useState<"passage" | "questions">("passage");

  const questionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const questionsScrollContainerRef = useRef<HTMLDivElement | null>(null);

  const safePageIndex = Math.min(
    Math.max(0, activePageIndex),
    Math.max(0, pagesList.length - 1),
  );
  const activePage = pagesList[safePageIndex] || pagesList[0];

  // Calculate starting question index for each page
  const pageOffsets = useMemo(() => {
    let count = 0;
    return pagesList.map((page) => {
      const start = count;
      count += page.subQuestions?.length || 0;
      return start;
    });
  }, [pagesList]);

  // Active page question statistics
  const activePageQuestions = activePage.subQuestions || [];
  const activePageAnsweredCount = activePageQuestions.filter((q) =>
    isReadingAnswerComplete(q, value[q.id]),
  ).length;

  const startQuestionNum = numberOffset + (pageOffsets[safePageIndex] || 0) + 1;
  const endQuestionNum =
    numberOffset + (pageOffsets[safePageIndex] || 0) + activePageQuestions.length;

  // Reading time estimate for active page
  const { wordCount, readTimeMin: readTimeMinutes } = useMemo(
    () => estimateReadingStats(activePage.passageText || ""),
    [activePage.passageText],
  );

  const change = (questionId: string, answer: ReadingAnswer) =>
    onChange({ ...value, [questionId]: answer });

  // Jump to specific question, switching page if necessary
  const jumpToQuestion = (targetPageIndex: number, questionId: string) => {
    if (targetPageIndex !== safePageIndex) {
      setActivePageIndex(targetPageIndex);
    }
    setMobileTab("questions");

    requestAnimationFrame(() => {
      setTimeout(() => {
        const node = questionRefs.current[questionId];
        if (node) {
          node.scrollIntoView({ behavior: "smooth", block: "start" });
          node.focus?.();
        }
      }, 50);
    });
  };

  // Re-runs on every new request (seq), not on every jumpToQuestion identity.
  useEffect(() => {
    if (!focusRequest) return;
    const pageIndex = pagesList.findIndex((page) =>
      page.subQuestions?.some((q) => q.id === focusRequest.questionId),
    );
    if (pageIndex >= 0) jumpToQuestion(pageIndex, focusRequest.questionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest?.seq]);

  const pageTitle = (page: PassagePage, index: number) =>
    page.passageTitle?.trim() || `${t("passage")} ${index + 1}`;

  return (
    <div className="reading-player flex min-w-0 flex-col">
      {/* Mobile: one pane at a time */}
      <div
        className="mb-4 flex gap-1 rounded-[14px] bg-[#EEF1F8] p-1 lg:hidden"
        role="tablist"
        aria-label={t("reading_view")}
      >
        {(["passage", "questions"] as const).map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            id={`${id}-tab-${item}`}
            aria-selected={mobileTab === item}
            aria-controls={`${id}-${item}`}
            tabIndex={mobileTab === item ? 0 : -1}
            onClick={() => setMobileTab(item)}
            onKeyDown={(event) => {
              if (
                ["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)
              ) {
                event.preventDefault();
                const next =
                  event.key === "Home"
                    ? "passage"
                    : event.key === "End"
                      ? "questions"
                      : item === "passage"
                        ? "questions"
                        : "passage";
                setMobileTab(next);
                document.getElementById(`${id}-tab-${next}`)?.focus();
              }
            }}
            className={cn(
              "h-10 flex-1 rounded-[11px] px-4 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#10182B]",
              mobileTab === item
                ? "bg-white text-[#10182B] shadow-[0_1px_3px_rgba(16,24,43,0.15)]"
                : "text-[#4B5468] hover:text-[#10182B]",
            )}
          >
            {item === "passage"
              ? t("reading_passage")
              : `${t("questions")} ${activePageAnsweredCount}/${activePageQuestions.length}`}
          </button>
        ))}
      </div>

      {/* Passage | divider | questions, each side scrolls on its own */}
      <div className="grid min-w-0 lg:h-[calc(100vh-210px)] lg:min-h-[520px] lg:grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)]">
        <section
          id={`${id}-passage`}
          aria-labelledby={`${id}-tab-passage`}
          className={cn(
            "flex min-h-0 min-w-0 flex-col",
            mobileTab !== "passage" && !disabled && "hidden lg:flex",
          )}
        >
          <div className="flex h-9 shrink-0 items-center justify-between gap-3 text-xs text-muted-foreground lg:pr-10">
            <span className="truncate tabular-nums">
              {wordCount > 0 &&
                `${wordCount} ${t("words")} · ~${readTimeMinutes} ${t("min_read")}`}
            </span>
            <div
              className="flex shrink-0 items-center"
              role="group"
              aria-label={t("font_size")}
            >
              {TEXT_SIZES.map(({ size, title, glyph }) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => setTextSize(size)}
                  title={title}
                  aria-label={title}
                  aria-pressed={textSize === size}
                  className={cn(
                    "flex h-7 w-7 items-center justify-center rounded-md font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    glyph,
                    textSize === size
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  A
                </button>
              ))}
            </div>
          </div>

          <div className="min-h-0 flex-1 select-text overflow-y-auto pb-10 pt-3 lg:pr-10">
            <h2 className="mb-5 break-words font-display text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
              {pageTitle(activePage, safePageIndex)}
            </h2>
            {(renderPassage ?? ((passage) => passage))(
              <div
                className={cn(
                  "whitespace-pre-wrap font-serif text-foreground sm:font-sans",
                  textSize === "sm" && "text-sm leading-7",
                  textSize === "md" && "text-base leading-8",
                  textSize === "lg" && "text-lg leading-9",
                )}
              >
                <RenderedHtml
                  html={activePage.passageText || ""}
                  className="prose dark:prose-invert max-w-none"
                />
              </div>,
              safePageIndex,
            )}
          </div>
          {/* Phones show one pane at a time: a way across once the passage has been read. */}
          <button
            type="button"
            onClick={() => setMobileTab("questions")}
            className="mb-2 ml-auto mt-2 inline-flex h-12 shrink-0 items-center rounded-full bg-[#D1FE17] px-[18px] text-sm font-extrabold text-[#10182B] shadow-[0_12px_24px_-10px_rgba(16,24,43,0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#10182B] lg:hidden"
          >
            {t("exam.reading.go_questions")} →
          </button>
        </section>

        <div aria-hidden className="hidden bg-border lg:block" />

        <section
          id={`${id}-questions`}
          aria-labelledby={`${id}-tab-questions`}
          className={cn(
            "flex min-h-0 min-w-0 flex-col",
            mobileTab !== "questions" && !disabled && "hidden lg:flex",
          )}
        >
          <div className="flex h-9 shrink-0 items-center justify-between gap-3 text-xs text-muted-foreground lg:pl-10">
            <span className="font-semibold uppercase tracking-wider">
              {activePageQuestions.length > 0
                ? `${t("questions")} ${startQuestionNum}–${endQuestionNum}`
                : t("questions")}
            </span>
            <span className="tabular-nums">
              {activePageAnsweredCount}/{activePageQuestions.length}{" "}
              {t("answered")}
            </span>
          </div>

          <div
            ref={questionsScrollContainerRef}
            className="min-h-0 flex-1 space-y-3 overflow-y-auto pb-10 pt-3 lg:pl-10 lg:pr-1"
          >
            {activePageQuestions.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                {t("no_questions_in_passage")}
              </p>
            ) : (
              activePageQuestions.map((question, localIndex) => {
                const globalNumber =
                  numberOffset + (pageOffsets[safePageIndex] || 0) + localIndex + 1;
                const answer = value[question.id];
                const isAnswered = isReadingAnswerComplete(question, answer);
                const options = question.options ?? [];
                const points = question.points || 1;

                return (
                  <div
                    key={question.id}
                    ref={(node) => {
                      questionRefs.current[question.id] = node;
                    }}
                    tabIndex={-1}
                    className="scroll-mt-3 rounded-xl border border-border/70 bg-card p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:p-5"
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={cn(
                          "mt-0.5 flex h-6 min-w-6 shrink-0 items-center justify-center rounded-md px-1.5 text-xs font-semibold tabular-nums transition-colors",
                          isAnswered
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground",
                        )}
                      >
                        {globalNumber}
                      </span>

                      <div className="min-w-0 flex-1 space-y-3">
                        {(question.type !== "FILL_IN_BLANKS" ||
                          !question.blanks?.length) && (
                          <div className="text-[15px] leading-relaxed text-foreground">
                            <RenderedHtml
                              html={question.questionText || question.prompt || ""}
                              inline
                            />
                          </div>
                        )}

                        {question.maxWords && (
                          <p className="text-xs font-medium text-muted-foreground">
                            {t("reading_word_limit", { count: question.maxWords })}
                          </p>
                        )}

                        {(question.type === "MULTIPLE_CHOICE" ||
                          question.type === "TRUE_FALSE") && (
                          <RadioGroup
                            value={typeof answer === "string" ? answer : ""}
                            disabled={disabled}
                            onValueChange={(next) => change(question.id, next)}
                            className="gap-0.5"
                          >
                            {options.map((option) => {
                              const itemId = `${id}-${question.id}-${option.id}`;
                              return (
                                <label
                                  key={option.id}
                                  htmlFor={itemId}
                                  className={optionRowClass(
                                    answer === option.id,
                                    disabled,
                                  )}
                                >
                                  <RadioGroupItem value={option.id} id={itemId} />
                                  <span className="flex-1">
                                    <RenderedHtml
                                      html={getQuestionOptionLabel(
                                        question.type,
                                        option.value ?? option.text ?? "",
                                        t,
                                      )}
                                      inline
                                    />
                                  </span>
                                </label>
                              );
                            })}
                          </RadioGroup>
                        )}

                        {question.type === "MULTIPLE_RESPONSE" && (
                          <div className="grid gap-0.5">
                            {options.map((option) => {
                              const isSelected =
                                Array.isArray(answer) && answer.includes(option.id);
                              const itemId = `${id}-${question.id}-${option.id}`;
                              const toggle = (checked: boolean) => {
                                const current = Array.isArray(answer) ? answer : [];
                                change(
                                  question.id,
                                  checked
                                    ? [...current, option.id]
                                    : current.filter((item) => item !== option.id),
                                );
                              };
                              return (
                                <label
                                  key={option.id}
                                  htmlFor={itemId}
                                  className={optionRowClass(isSelected, disabled)}
                                >
                                  <Checkbox
                                    id={itemId}
                                    value={option.id}
                                    disabled={disabled}
                                    checked={isSelected}
                                    onCheckedChange={(checked) => toggle(checked === true)}
                                  />
                                  <input
                                    type="checkbox"
                                    value={option.id}
                                    disabled={disabled}
                                    checked={isSelected}
                                    tabIndex={-1}
                                    aria-hidden="true"
                                    className="sr-only"
                                    onChange={(event) => toggle(event.target.checked)}
                                  />
                                  <span className="flex-1">
                                    <RenderedHtml
                                      html={option.value ?? option.text ?? ""}
                                      inline
                                    />
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}

                        {question.type === "MATCHING" && (
                          <MatchingPlayer
                            pairs={question.matchPairs ?? []}
                            options={question.matchingOptions}
                            value={asMap(answer)}
                            onChange={(next) => change(question.id, next)}
                            disabled={disabled}
                          />
                        )}

                        {question.type === "FILL_IN_BLANKS" &&
                          Boolean(question.blanks?.length) && (
                            <FillInBlanksPlayer
                              prompt={question.questionText || question.prompt || ""}
                              blanks={question.blanks ?? []}
                              value={asMap(answer)}
                              onChange={(next) => change(question.id, next)}
                              disabled={disabled}
                            />
                          )}

                        {(question.type === "SHORT_ANSWER" ||
                          (question.type === "FILL_IN_BLANKS" &&
                            !question.blanks?.length)) && (
                          <input
                            aria-label={`${t("reading_answer")} ${globalNumber}`}
                            type="text"
                            autoComplete="off"
                            disabled={disabled}
                            placeholder={t("type_your_answer_here")}
                            value={typeof answer === "string" ? answer : ""}
                            onChange={(event) =>
                              change(question.id, event.target.value)
                            }
                            className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm focus-visible:!outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          />
                        )}
                      </div>

                      {points > 1 && (
                        <span className="shrink-0 pt-1 text-xs text-muted-foreground">
                          {points} {t("pts")}
                        </span>
                      )}

                      {onToggleFlag && (
                        <button
                          type="button"
                          onClick={() => onToggleFlag(question.id)}
                          aria-pressed={flaggedIds?.has(question.id) ?? false}
                          className={cn(
                            "inline-flex h-[30px] shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            flaggedIds?.has(question.id)
                              ? "border-[#F5B5B0] bg-[#FDECEC] text-[#B42318]"
                              : "border-[#E6E9F2] bg-white text-[#4B5468] hover:bg-[#F4F6FB]",
                          )}
                        >
                          <span aria-hidden>⚑</span>
                          {t(flaggedIds?.has(question.id) ? "exam.flag.flagged" : "exam.flag.flag")}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>

      {/* One slim navigator row: passages (when several), then question numbers */}
      <nav
        aria-label={t("question_navigator")}
        className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border/70 pt-3"
      >
        {pagesList.length > 1 && (
          <div className="flex min-w-0 flex-wrap items-center gap-1">
            {pagesList.map((page, pIdx) => {
              const isActive = pIdx === safePageIndex;
              const pQuestions = page.subQuestions || [];
              const pAnswered = pQuestions.filter((q) =>
                isReadingAnswerComplete(q, value[q.id]),
              ).length;
              return (
                <button
                  key={page.id}
                  type="button"
                  onClick={() => {
                    setActivePageIndex(pIdx);
                    setMobileTab("passage");
                  }}
                  title={pageTitle(page, pIdx)}
                  aria-current={isActive ? "page" : undefined}
                  className={cn(
                    "flex h-8 min-w-0 max-w-[200px] items-center gap-2 rounded-md px-2.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <span className="truncate">{pageTitle(page, pIdx)}</span>
                  {pQuestions.length > 0 && (
                    <span className="shrink-0 tabular-nums opacity-70">
                      {pAnswered}/{pQuestions.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-1">
          {activePageQuestions.map((q, localIndex) => {
            const globalNumber =
              numberOffset + (pageOffsets[safePageIndex] || 0) + localIndex + 1;
            const answered = isReadingAnswerComplete(q, value[q.id]);
            return (
              <button
                key={q.id}
                type="button"
                onClick={() => jumpToQuestion(safePageIndex, q.id)}
                aria-label={`${t("question")} ${globalNumber}: ${t(answered ? "answered" : "unanswered")}${flaggedIds?.has(q.id) ? `, ${t("exam.flag.flagged")}` : ""}`}
                className={cn(
                  "relative h-8 min-w-8 rounded-md px-2 text-xs tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  flaggedIds?.has(q.id) &&
                    "after:absolute after:right-0 after:top-0 after:h-0 after:w-0 after:rounded-tr-md after:border-l-[9px] after:border-t-[9px] after:border-l-transparent after:border-t-[#B42318] after:content-['']",
                  answered
                    ? "bg-primary font-semibold text-primary-foreground"
                    : "text-muted-foreground ring-1 ring-inset ring-border hover:bg-muted hover:text-foreground",
                )}
              >
                {globalNumber}
              </button>
            );
          })}
        </div>

        {pagesList.length > 1 && (
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={safePageIndex === 0}
              onClick={() => {
                setActivePageIndex((prev) => Math.max(0, prev - 1));
                setMobileTab("passage");
              }}
              aria-label={t("previous_passage")}
              title={t("previous_passage")}
              className="h-8 w-8"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={safePageIndex === pagesList.length - 1}
              onClick={() => {
                setActivePageIndex((prev) =>
                  Math.min(pagesList.length - 1, prev + 1),
                );
                setMobileTab("passage");
              }}
              aria-label={t("next_passage")}
              title={t("next_passage")}
              className="h-8 w-8"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </nav>
    </div>
  );
}

export default PassagePlayer;
